/**
 * RoutineFormFields — input fields for the routine create/alter form. Split
 * from `RoutineEditFormBody` to keep the orchestrator under the 200-line
 * CLAUDE.md budget.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7.
 */
import type { Dispatch, SetStateAction } from 'react';

import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import {
  KIND_OPTIONS,
  SECURITY_OPTIONS,
  SQL_ACCESS_OPTIONS,
  VOLATILITY_OPTIONS,
  type RoutineEditMode,
  type RoutineFormValues,
} from './routineFormHelpers';
import { RoutineParamRows } from './RoutineParamRows';

/**
 * Radix Select reserves `''`. The volatility / security / SQL-access option
 * lists each carry a `''` "— default —" entry that `buildRoutineShellDef`
 * reads as "omit the clause", so `''` travels through the Select as this
 * sentinel and is mapped back to `''` on the way out.
 */
const UNSET = '__unset__' as const;

export interface RoutineFormFieldsProps {
  readonly engine: SqlEngine;
  readonly mode: RoutineEditMode;
  readonly values: RoutineFormValues;
  readonly setValues: Dispatch<SetStateAction<RoutineFormValues>>;
  readonly testId: string;
}

export function RoutineFormFields(props: RoutineFormFieldsProps) {
  const { engine, mode, values, setValues, testId } = props;

  const update = <K extends keyof RoutineFormValues>(
    key: K, value: RoutineFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  const modeAllowedOnFunctionParams = !(values.kind === 'function'
    && (engine === 'mssql' || engine === 'mysql'));

  return (
    <>
      {mode === 'create' ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px]">Kind</Label>
            <Select
              value={values.kind}
              onValueChange={(v) => update('kind', v as RoutineFormValues['kind'])}
            >
              <SelectTrigger className="h-7 py-0 text-xs" data-testid={`${testId}-kind`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${testId}-name`} className="text-[11px]">Name</Label>
            <Input
              id={`${testId}-name`}
              data-testid={`${testId}-name`}
              className="h-7 text-xs"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${testId}-name`} className="text-[11px]">Routine name</Label>
          <Input
            id={`${testId}-name`}
            data-testid={`${testId}-name`}
            className="h-7 text-xs"
            value={values.name}
            onChange={(e) => update('name', e.target.value)}
          />
        </div>
      )}

      {mode === 'create' ? (
        <>
          <RoutineParamRows
            engine={engine}
            params={values.params}
            onChange={(next) => update('params', next)}
            testId={`${testId}-params`}
            modeAllowed={modeAllowedOnFunctionParams}
          />

          {values.kind === 'function' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${testId}-returns`} className="text-[11px]">
                RETURNS (e.g. <code>INT</code>, <code>numeric(12,2)</code>)
              </Label>
              <Input
                id={`${testId}-returns`}
                data-testid={`${testId}-returns`}
                className="h-7 font-mono text-xs"
                value={values.returnsText}
                onChange={(e) => update('returnsText', e.target.value)}
              />
            </div>
          ) : null}

          {engine === 'postgres' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${testId}-language`} className="text-[11px]">
                LANGUAGE (e.g. <code>plpgsql</code>, <code>sql</code>)
              </Label>
              <Input
                id={`${testId}-language`}
                data-testid={`${testId}-language`}
                className="h-7 font-mono text-xs"
                value={values.pgsqlLanguage}
                onChange={(e) => update('pgsqlLanguage', e.target.value)}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${testId}-body`} className="text-[11px]">
              Body (opaque text — switch to "Edit as SQL" for the procedural grammar)
            </Label>
            <textarea
              id={`${testId}-body`}
              data-testid={`${testId}-body`}
              className="min-h-[80px] rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
              value={values.bodyText}
              onChange={(e) => update('bodyText', e.target.value)}
            />
          </div>

          <RoutineEngineFlags engine={engine} values={values} update={update} testId={testId} />
        </>
      ) : (
        <RoutineAlterPicker engine={engine} values={values} update={update} testId={testId} />
      )}
    </>
  );
}

interface RoutineEngineFlagsProps {
  readonly engine: SqlEngine;
  readonly values: RoutineFormValues;
  readonly update: <K extends keyof RoutineFormValues>(k: K, v: RoutineFormValues[K]) => void;
  readonly testId: string;
}

function RoutineEngineFlags({ engine, values, update, testId }: RoutineEngineFlagsProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px]">
      {engine === 'mssql' ? (
        <label className="flex items-center gap-1">
          <Checkbox
            data-testid={`${testId}-or-alter`}
            checked={values.mssqlOrAlter}
            onCheckedChange={(checked) => update('mssqlOrAlter', checked === true)}
          />
          OR ALTER
        </label>
      ) : null}
      {engine === 'postgres' || engine === 'oracle' ? (
        <label className="flex items-center gap-1">
          <Checkbox
            data-testid={`${testId}-or-replace`}
            checked={values.orReplace}
            onCheckedChange={(checked) => update('orReplace', checked === true)}
          />
          OR REPLACE
        </label>
      ) : null}
      {engine === 'oracle' ? (
        <label className="flex items-center gap-1">
          <Checkbox
            data-testid={`${testId}-editionable`}
            checked={values.oracleEditionable}
            onCheckedChange={(checked) => update('oracleEditionable', checked === true)}
          />
          EDITIONABLE
        </label>
      ) : null}
      {engine === 'postgres' && values.kind === 'function' ? (
        <label className="flex items-center gap-1">
          Volatility
          <Select
            value={values.pgsqlVolatility === '' ? UNSET : values.pgsqlVolatility}
            onValueChange={(v) =>
              update('pgsqlVolatility', (v === UNSET ? '' : v) as RoutineFormValues['pgsqlVolatility'])
            }
          >
            <SelectTrigger className="h-6 w-auto px-2 py-0 text-[11px]" data-testid={`${testId}-volatility`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VOLATILITY_OPTIONS.map((o) => (
                <SelectItem key={o.value || '_default'} value={o.value || UNSET}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : null}
      {engine === 'postgres' && values.kind === 'function' ? (
        <label className="flex items-center gap-1">
          <Checkbox
            data-testid={`${testId}-strict`}
            checked={values.pgsqlStrict}
            onCheckedChange={(checked) => update('pgsqlStrict', checked === true)}
          />
          STRICT
        </label>
      ) : null}
      {engine === 'postgres' ? (
        <label className="flex items-center gap-1">
          Security
          <Select
            value={values.pgsqlSecurity === '' ? UNSET : values.pgsqlSecurity}
            onValueChange={(v) =>
              update('pgsqlSecurity', (v === UNSET ? '' : v) as RoutineFormValues['pgsqlSecurity'])
            }
          >
            <SelectTrigger className="h-6 w-auto px-2 py-0 text-[11px]" data-testid={`${testId}-pg-security`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECURITY_OPTIONS.map((o) => (
                <SelectItem key={o.value || '_default'} value={o.value || UNSET}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : null}
      {engine === 'mysql' ? (
        <label className="flex items-center gap-1">
          <Checkbox
            data-testid={`${testId}-deterministic`}
            checked={values.mysqlDeterministic}
            onCheckedChange={(checked) => update('mysqlDeterministic', checked === true)}
          />
          DETERMINISTIC
        </label>
      ) : null}
      {engine === 'mysql' ? (
        <label className="flex items-center gap-1">
          SQL access
          <Select
            value={values.mysqlSqlAccess === '' ? UNSET : values.mysqlSqlAccess}
            onValueChange={(v) =>
              update('mysqlSqlAccess', (v === UNSET ? '' : v) as RoutineFormValues['mysqlSqlAccess'])
            }
          >
            <SelectTrigger className="h-6 w-auto px-2 py-0 text-[11px]" data-testid={`${testId}-sql-access`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SQL_ACCESS_OPTIONS.map((o) => (
                <SelectItem key={o.value || '_default'} value={o.value || UNSET}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : null}
    </div>
  );
}

interface RoutineAlterPickerProps {
  readonly engine: SqlEngine;
  readonly values: RoutineFormValues;
  readonly update: <K extends keyof RoutineFormValues>(k: K, v: RoutineFormValues[K]) => void;
  readonly testId: string;
}

function RoutineAlterPicker({ engine, values, update, testId }: RoutineAlterPickerProps) {
  if (engine === 'oracle') {
    return (
      <div className="text-[11px] text-muted-foreground" data-testid={`${testId}-alter-action`}>
        Will emit <code className="font-mono">ALTER {values.kind.toUpperCase()} … COMPILE</code>.
      </div>
    );
  }
  if (engine === 'postgres') {
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${testId}-rename-to`} className="text-[11px]">RENAME TO</Label>
        <Input
          id={`${testId}-rename-to`}
          data-testid={`${testId}-rename-to`}
          className="h-7 text-xs"
          value={values.alterRenameTo}
          onChange={(e) => update('alterRenameTo', e.target.value)}
        />
      </div>
    );
  }
  return null;
}
